// ---- Writes --------------------------------------------------------------

import { useMutation, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { serviceApi, vmPath, vmKeys, type VmwareAction } from '../_shared';

/** Optional context passed alongside a perform verb (e.g. resize cpu/memory/disk). */
export type PerformContext = Record<string, unknown>;

interface PerformInput {
  perform: VmwareAction;
  context?: PerformContext;
}

/**
 * Power/resize action → `POST /instances/:id/perform` with `{ perform, context }`.
 * VMware verbs: start / reboot / shutdown, and `update` (resize) with a context.
 */
export function useInstanceAction(
  serviceId: string,
  instanceId: string,
  options?: UseMutationOptions<unknown, unknown, PerformInput>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ perform, context }: PerformInput) =>
      (await serviceApi.post(vmPath(serviceId, `instances/${instanceId}/perform`), { perform, context })).data,
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: vmKeys.instance(serviceId, instanceId) });
      qc.invalidateQueries({ queryKey: vmKeys.instances(serviceId) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Resize convenience: `update` verb carrying a cpu/memory/disk context. */
export function useInstanceResize(
  serviceId: string,
  instanceId: string,
  options?: UseMutationOptions<unknown, unknown, PerformContext>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (context: PerformContext) =>
      (await serviceApi.post(vmPath(serviceId, `instances/${instanceId}/perform`), { perform: 'update', context }))
        .data,
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: vmKeys.instance(serviceId, instanceId) });
      qc.invalidateQueries({ queryKey: vmKeys.instances(serviceId) });
      options?.onSuccess?.(...args);
    },
  });
}
